import { GpoOrderRow, useGetGpoOrdersApi } from 'apis/gpo-orders';
import { BaseTableContainer, ExcelTable } from 'components/base-table';
import {
  BaseTableSpinner,
  BaseTableTitle,
  ClearFilterButton,
  CustomTableCell,
  TableCell,
  TableHeader,
} from 'components/base-table/components';
import { RestaurantIdFilterButton } from 'components/base-table/filters';
import { BaseSortButton } from 'components/base-table/sorting';
import {
  Badge,
  BasePagination,
  Box,
  Flex,
  IconButtonWithTooltip,
  PenIcon,
  Text,
  useBreakpointValue,
} from 'design-system';
import { useSetPaginationParamsV1 } from 'hooks/use-set-pagination-params-v1';
import { DateRangeFilterButton } from 'pages/gpo-orders/components/date-range-filter-button';
import {
  IOverrideCommissionModalCell,
  OverrideCommissionModal,
} from 'pages/gpo-orders/components/override-commission-modal';
import { SupplierFilterButton } from 'pages/gpo-orders/components/supplier-filter-button';
import { commissionBadgeColor, commissionBadgeLabel, sourceLabels } from 'pages/gpo-orders/utils/labels';
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CellProps } from 'react-table';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';

interface IColumnsAccessorData {
  id: number;
  order: { code: string; createdAt: string };
  restaurantName: string | null;
  supplierName: string | null;
  total: number;
  commission: GpoOrderRow;
  source: GpoOrderRow;
  actions: GpoOrderRow;
}

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

const CardLabel = ({ children }: { children: React.ReactNode }) => (
  <Text fontSize='10.5px' fontWeight='semibold' letterSpacing='0.08em' textTransform='uppercase' color='gray.500'>
    {children}
  </Text>
);

interface IGpoOrderCardItem {
  order: { code: string; createdAt: string };
  restaurantName: string | null;
  supplierName: string | null;
  total: number;
  commission: GpoOrderRow;
}

const GpoOrderCard = ({ item }: { item: IGpoOrderCardItem }) => {
  const row = item.commission;

  return (
    <Box bg='white' borderRadius='14px' border='1px solid' borderColor='gray.100' p='14px' mb='10px'>
      <Flex align='flex-start' gap='8px'>
        <Box flex='1' minW={0}>
          <Link to={`/app/orders/details/${item.order.code}`}>
            <Text fontWeight='semibold' color='red.500' fontSize='14px' noOfLines={1}>
              {item.order.code}
            </Text>
          </Link>
          <Text fontSize='12px' color='gray.500'>
            {formatDate(item.order.createdAt)}
          </Text>
        </Box>
        <Box flexShrink={0}>
          <OverrideCommissionModal
            row={row}
            trigger={
              <IconButtonWithTooltip
                size='sm'
                minW='42px'
                minH='42px'
                label='Edit commission'
                aria-label='edit-commission-btn'
                Icon={<PenIcon />}
                variant='unstyled'
              />
            }
          />
        </Box>
      </Flex>

      <Flex gap='12px' mt='10px'>
        <Box flex='1' minW={0}>
          <CardLabel>Restaurant</CardLabel>
          <Text fontSize='13px' noOfLines={2}>
            {item.restaurantName ?? '—'}
          </Text>
        </Box>
        <Box flex='1' minW={0}>
          <CardLabel>Supplier</CardLabel>
          <Text fontSize='13px' noOfLines={2}>
            {item.supplierName ?? '—'}
          </Text>
        </Box>
      </Flex>

      <Flex gap='12px' mt='10px'>
        <Box flex='1' minW={0}>
          <CardLabel>Order Total</CardLabel>
          <Text fontSize='13px' whiteSpace='nowrap'>
            {item.total.toFixed(2)}
          </Text>
        </Box>
        <Box flex='1' minW={0}>
          <CardLabel>Commission</CardLabel>
          {row.commissionRate == null && row.commissionAmount == null ? (
            <Text fontSize='13px' color='gray.400'>
              No rate yet
            </Text>
          ) : (
            <>
              <Text fontSize='13px' whiteSpace='nowrap'>
                {row.commissionAmount != null ? row.commissionAmount.toFixed(2) : '—'}
                {row.commissionType && (
                  <Badge ml='2' colorScheme={commissionBadgeColor(row.commissionType)} variant='subtle'>
                    {commissionBadgeLabel(row.commissionType)}
                  </Badge>
                )}
              </Text>
              <Text fontSize='12px' color='gray.500'>
                {row.commissionRate != null ? `${row.commissionRate}%` : 'no rate'}
              </Text>
            </>
          )}
        </Box>
      </Flex>

      <Box mt='10px'>
        <CardLabel>Source</CardLabel>
        <Text fontSize='13px'>
          {sourceLabels[row.source]}
          {row.overriddenByName ? ` · by ${row.overriddenByName}` : ''}
        </Text>
      </Box>
    </Box>
  );
};

export const GpoOrdersListScreen = () => {
  const setPaginationParams = useSetPaginationParamsV1();
  const dispatch = useAppDispatch();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const { isLoading, isFetching, data } = useGetGpoOrdersApi();

  const { pageCount, total, data: rows } = data ?? {};

  useEffect(() => {
    dispatch(changeTitle('GPO Orders'));
  }, []);

  const columns = React.useMemo(
    () => [
      {
        Header: (
          <TableHeader
            name='Order'
            filterButtonComponent={<DateRangeFilterButton />}
            sortButtonComponent={<BaseSortButton sortBy='date' />}
          />
        ),
        accessor: 'order',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['order']>) => (
          <div>
            <Link to={`/app/orders/details/${value.code}`}>
              <Text fontWeight='semibold' color='red.500'>
                {value.code}
              </Text>
            </Link>
            <Text fontSize='xs' color='gray.500'>
              {formatDate(value.createdAt)}
            </Text>
          </div>
        ),
      },
      {
        Header: <TableHeader name='Restaurant' filterButtonComponent={<RestaurantIdFilterButton />} />,
        accessor: 'restaurantName',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['restaurantName']>) => <TableCell value={value ?? '—'} />,
      },
      {
        Header: <TableHeader name='Supplier' filterButtonComponent={<SupplierFilterButton />} />,
        accessor: 'supplierName',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['supplierName']>) => <TableCell value={value ?? '—'} />,
      },
      {
        Header: <TableHeader name='Order Total' sortButtonComponent={<BaseSortButton sortBy='total' />} />,
        accessor: 'total',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['total']>) => (
          <TableCell value={value.toFixed(2)} whiteSpace='nowrap' />
        ),
      },
      {
        Header: <TableHeader name='Commission' sortButtonComponent={<BaseSortButton sortBy='commissionAmount' />} />,
        accessor: 'commission',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['commission']>) => {
          if (value.commissionRate == null && value.commissionAmount == null) {
            return <Text color='gray.400'>No rate yet</Text>;
          }
          return (
            <div>
              <Text whiteSpace='nowrap'>
                {value.commissionAmount != null ? value.commissionAmount.toFixed(2) : '—'}
                {value.commissionType && (
                  <Badge ml='2' colorScheme={commissionBadgeColor(value.commissionType)} variant='subtle'>
                    {commissionBadgeLabel(value.commissionType)}
                  </Badge>
                )}
              </Text>
              <Text fontSize='xs' color='gray.500'>
                {value.commissionRate != null ? `${value.commissionRate}%` : 'no rate'}
              </Text>
            </div>
          );
        },
      },
      {
        Header: <TableHeader name='Source' />,
        accessor: 'source',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['source']>) => (
          <div>
            <Text fontSize='sm'>{sourceLabels[value.source]}</Text>
            {value.overriddenByName && (
              <Text fontSize='xs' color='gray.500'>
                by {value.overriddenByName}
              </Text>
            )}
          </div>
        ),
      },
      {
        Header: <TableHeader name='Actions' />,
        accessor: 'actions',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['actions']>) => (
          <CustomTableCell<IOverrideCommissionModalCell>
            as={OverrideCommissionModal}
            row={value}
            trigger={
              <IconButtonWithTooltip
                size='sm'
                label='Edit commission'
                aria-label='edit-commission-btn'
                Icon={<PenIcon />}
                variant='unstyled'
              />
            }
          />
        ),
      },
    ],
    []
  );

  const tableData = React.useMemo(
    () =>
      (rows ?? []).map((row) => ({
        id: row.orderId,
        order: { code: row.orderCode, createdAt: row.orderCreatedAt },
        restaurantName: row.restaurantName,
        supplierName: row.supplierName,
        total: row.orderTotal,
        commission: row,
        source: row,
        actions: row,
      })),
    [rows]
  );

  return (
    <BaseTableContainer isLoading={isLoading}>
      <BaseTableContainer.Header>
        <BaseTableContainer.Header.Left>
          <BaseTableTitle title='GPO Orders' />
          <BaseTableSpinner isFetching={isFetching} />
        </BaseTableContainer.Header.Left>

        <BaseTableContainer.Header.Right>
          <ClearFilterButton />
        </BaseTableContainer.Header.Right>
      </BaseTableContainer.Header>

      {isMobile ? (
        // Cards live outside TableWrapper: TableContainer's whiteSpace='nowrap' would leak into card text.
        <Box flexGrow={1} overflowY='auto' minH='200px' px='sm' pb='sm'>
          <Flex display={{ base: 'flex', md: 'none' }} flexWrap='wrap' gap='4px' align='center' mb='10px'>
            <DateRangeFilterButton />
            <RestaurantIdFilterButton />
            <SupplierFilterButton />
            <BaseSortButton sortBy='date' asMenu label='date' />
            <BaseSortButton sortBy='total' asMenu label='order total' />
            <BaseSortButton sortBy='commissionAmount' asMenu label='commission' />
          </Flex>
          {tableData.length === 0 ? (
            <Box bg='white' borderRadius='14px' border='1px solid' borderColor='gray.100' p='14px'>
              <Text fontSize='13px' color='gray.500' textAlign='center'>
                No orders found
              </Text>
            </Box>
          ) : (
            tableData.map((item) => <GpoOrderCard key={item.id} item={item} />)
          )}
        </Box>
      ) : (
        <BaseTableContainer.TableWrapper>
          <ExcelTable columns={columns} data={tableData} />
        </BaseTableContainer.TableWrapper>
      )}

      <BaseTableContainer.Footer>
        <BaseTableContainer.Footer.Left />
        <BaseTableContainer.Footer.Right>
          <BasePagination>
            <BasePagination.Pages pagesCount={pageCount ?? 0} setSearchParams={setPaginationParams} />
            <BasePagination.Limit total={total ?? 0} setSearchParams={setPaginationParams} />
          </BasePagination>
        </BaseTableContainer.Footer.Right>
      </BaseTableContainer.Footer>
    </BaseTableContainer>
  );
};
